/**
 * ReasonSelector component
 *
 * Multi-select checkboxes for vote reasons
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { YES_REASONS, NO_REASONS } from '@doyoulikethis/shared';

interface ReasonSelectorProps {
  liked: boolean;
  onSubmit: (reasons: string[]) => void;
  loading?: boolean;
}

export function ReasonSelector({
  liked,
  onSubmit,
  loading = false,
}: ReasonSelectorProps) {
  const { t, i18n } = useTranslation();
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const isJapanese = i18n.language === 'ja';

  const reasons = liked ? YES_REASONS : NO_REASONS;
  const reasonEntries = Object.entries(reasons) as [string, { en: string; ja: string }][];

  const toggleReason = (code: string) => {
    setSelectedReasons((prev) =>
      prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code]
    );
  };

  const handleSubmit = () => {
    onSubmit(selectedReasons);
  };

  return (
    <div className="mx-auto w-full max-w-lg animate-fade-in">
      {/* Prompt */}
      <div className="mb-6 text-center">
        <h2 className="text-xl font-semibold">
          {liked ? t('reasons.prompt_yes') : t('reasons.prompt_no')}
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-200">
          {t('reasons.select_all')}
        </p>
      </div>

      {/* Reason checkboxes */}
      <fieldset>
        <legend className="sr-only">
          {liked ? t('reasons.prompt_yes') : t('reasons.prompt_no')}
        </legend>
        <div className="space-y-3" role="group">
          {reasonEntries.map(([code, labels]) => (
            <label
              key={code}
              className={`
                flex cursor-pointer items-center gap-3 rounded-lg border p-4
                transition-colors
                focus-within:ring-2 focus-within:ring-accent focus-within:ring-offset-2
                ${
                  selectedReasons.includes(code)
                    ? 'border-accent bg-accent/5'
                    : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-600 dark:bg-black dark:hover:border-gray-500'
                }
              `}
            >
              <input
                type="checkbox"
                checked={selectedReasons.includes(code)}
                onChange={() => toggleReason(code)}
                className="h-5 w-5 rounded border-gray-300 text-accent focus:ring-0 focus:ring-offset-0"
                aria-label={isJapanese ? labels.ja : labels.en}
              />
              <span className="flex-1 text-sm sm:text-base">
                {isJapanese ? labels.ja : labels.en}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Submit button */}
      <div className="mt-6 flex justify-center">
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          loading={loading}
          disabled={loading}
          className="group"
        >
          {t('reasons.continue')}
          <ArrowRight
            className="h-5 w-5 transition-transform group-hover:translate-x-1"
            aria-hidden="true"
          />
        </Button>
      </div>

      {/* Note about optional selection */}
      <p className="mt-4 text-center text-xs text-gray-600 dark:text-gray-200">
        You can continue without selecting any reasons
      </p>
    </div>
  );
}
